import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { services as fallbackServices } from '../data';
import { useCms } from '../context/CmsContext';

// Helper to determine Font Awesome icon safely from service type
const getServiceIcon = (titleOrSlug, idx = 0) => {
  const str = String(titleOrSlug || '').toLowerCase();
  if (str.includes('fredede') || str.includes('kulturarv') || str.includes('historisk')) return 'fa-solid fa-landmark';
  if (str.includes('facade') || str.includes('mur') || str.includes('tegl')) return 'fa-solid fa-trowel-bricks';
  if (str.includes('graffiti') || str.includes('beton') || str.includes('natursten')) return 'fa-solid fa-spray-can';
  if (str.includes('rust') || str.includes('metal')) return 'fa-solid fa-screwdriver-wrench';
  if (str.includes('industri') || str.includes('skade')) return 'fa-solid fa-industry';
  const icons = ['fa-solid fa-landmark', 'fa-solid fa-trowel-bricks', 'fa-solid fa-spray-can', 'fa-solid fa-screwdriver-wrench', 'fa-solid fa-industry'];
  return icons[idx % icons.length];
};

// Helper to get matching standard route slug
const getServiceRouteSlug = (service, idx = 0) => {
  const sTitle = typeof service?.title === 'object' ? service.title?.da : service?.title || '';
  const sSlug = typeof service?.slug === 'object' ? service.slug?.da : service?.slug || '';
  const str = (sSlug + ' ' + sTitle).toLowerCase();
  if (str.includes('fredede')) return 'fredede-bygninger';
  if (str.includes('facade') || str.includes('mur')) return 'facaderens-murvaerk';
  if (str.includes('graffiti') || str.includes('beton')) return 'graffiti-beton-natursten';
  if (str.includes('rust') || str.includes('metal')) return 'rust-metaloverflader';
  if (str.includes('industri') || str.includes('skade')) return 'industri-skadeservice';
  const defaultSlugs = ['fredede-bygninger', 'facaderens-murvaerk', 'graffiti-beton-natursten', 'rust-metaloverflader', 'industri-skadeservice'];
  return defaultSlugs[idx % defaultSlugs.length];
};

export default function Services() {
  const { services } = useCms();
  const displayServices = services && services.length > 0 ? services : fallbackServices;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Laserrens & Facaderens | Se vores ydelser</title>
        <meta name="description" content="Se vores ydelser inden for laserrens: Fredede bygninger, nybyggeri facade, graffiti/beton/natursten, metal samt industri & skadeservice. Læs mere her ➔" />
        <meta property="og:title" content="Laserrens & Facaderens | Se vores ydelser" />
        <meta property="og:description" content="Se vores ydelser inden for laserrens: Fredede bygninger, nybyggeri facade, graffiti/beton/natursten, metal samt industri & skadeservice. Læs mere her ➔" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-4">
          {/* Environmental Badge */}
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/30 px-4 py-1.5 rounded-full text-brand-green text-xs font-semibold uppercase tracking-wider">
            <i className="fa-solid fa-droplet-slash text-brand-green animate-pulse" /> Skåner vores kostbare grundvand
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Ydelser inden for Højteknologisk Laserrens</h1>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-16">
            {displayServices.map((service, idx) => {
              const sSlug = getServiceRouteSlug(service, idx);
              const sTitle = typeof service.title === 'object' ? service.title?.da : service.title;
              const sDesc = typeof service.short_desc === 'object' ? service.short_desc?.da : service.short_desc;
              const sIcon = getServiceIcon(sTitle || sSlug, idx);
              const sImage = Array.isArray(service.image) && service.image.length > 0
                ? service.image[0]
                : typeof service.image === 'string'
                ? service.image
                : "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png";
              
              // Advantages parsing (supports array or newline-separated string)
              const rawAdv = typeof service.advantages === 'object' ? service.advantages?.da : service.advantages;
              const advantagesList = Array.isArray(rawAdv)
                ? rawAdv
                : typeof rawAdv === 'string'
                ? rawAdv.split('\n').filter(Boolean)
                : [];

              return (
                <div
                  key={sSlug}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center border-b border-zinc-100 pb-16 last:border-0 last:pb-0`}
                >
                  {/* Image Column */}
                  <div className={`lg:col-span-6 ${idx % 2 === 1 ? 'lg:order-last' : ''}`}>
                    <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-zinc-200 shadow-lg relative group">
                      <img
                        src={sImage.includes('assets.auxodev.dk') && !sImage.includes('/cdn-cgi/image/')
                          ? sImage.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/')
                          : sImage}
                        alt={sTitle}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                    </div>
                  </div>

                  {/* Text Column */}
                  <div className="lg:col-span-6 flex flex-col gap-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-green/10 border border-brand-green/20 flex items-center justify-center text-brand-green text-lg">
                        <i className={sIcon} />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-zinc-900">{sTitle}</h2>
                    </div>

                    <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
                      {sDesc}
                    </p>

                    {/* Advantages List */}
                    {advantagesList.length > 0 && (
                      <ul className="space-y-2">
                        {advantagesList.slice(0, 3).map((adv, i) => (
                          <li key={i} className="flex items-start gap-3 text-xs md:text-sm text-zinc-600">
                            <i className="fa-solid fa-circle-check text-brand-green mt-1 shrink-0" />
                            <span>{adv}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="pt-4 flex flex-col sm:flex-row items-center gap-4">
                      <Link
                        to={`/ydelser/${sSlug}`}
                        className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 text-center"
                      >
                        Læs mere om ydelsen
                      </Link>
                      <Link
                        to="/kontakt"
                        className="w-full sm:w-auto bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider border border-zinc-200 transition-all duration-300 text-center"
                      >
                        Få et tilbud
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-50 border-t border-zinc-200 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-5">
          <h2 className="text-2xl md:text-4xl font-bold text-zinc-900 leading-tight">
            Har du en specifik renseopgave?
          </h2>
          <p className="text-zinc-600 text-sm md:text-base max-w-xl leading-relaxed">
            Vi rådgiver dig gerne om det bedste materialevalg og valg af metode. Kontakt os i dag for en uforpligtende snak.
          </p>
          <Link
            to="/kontakt"
            className="bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_20px_rgba(118,184,42,0.3)] flex items-center gap-2 mt-2"
          >
            <i className="fa-solid fa-envelope" /> Kontakt os nu
          </Link>
        </div>
      </section>
    </>
  );
}
